/**
 * Campaign landing page cart behaviour.
 *
 * Progressive enhancement over the plain multi-line cart form: tap a flavour to
 * select it, steppers for quantity, a live total on the button, and an AJAX add
 * that confirms in place. Without this file the form still posts normally.
 *
 * All listeners are delegated from `document`. The theme re-renders sections
 * through sectionRenderer.renderSection(), which replaces their DOM outright —
 * anything bound directly to a row would silently stop working after that.
 *
 * The store runs cart_type: 'page', so there is no drawer to open. A normal add
 * would navigate to /cart and abandon the campaign; instead we confirm in place
 * and offer checkout. Cart-icon refresh is handed to the theme by dispatching
 * 'cart:update' (the string behind CartAddEvent.eventName) with the same detail
 * shape product-form.js uses.
 */
(function () {
  const ROOT = '.wlp-buy';
  const MAX = 20;

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const clamp = (n) => Math.max(0, Math.min(MAX, isNaN(n) ? 0 : n));
  const qtyOf = (input) => clamp(parseInt(input.value, 10));

  const money = (cents) =>
    window.Shopify && Shopify.formatMoney
      ? Shopify.formatMoney(cents, '${{amount}}')
      : '$' + (cents / 100).toFixed(2);

  const form = () => $('.wlp-buy__form');
  const rows = () => $$('.wlp-pick');
  const inputOf = (row) => $('.wlp-pick__input', row);

  function setQty(input, n) {
    if (!input) return;
    input.value = String(clamp(n));
    paint();
  }

  function totals() {
    return rows().reduce(
      (acc, row) => {
        const input = inputOf(row);
        if (!input) return acc;
        const q = qtyOf(input);
        acc.count += q;
        acc.cents += q * parseInt(row.dataset.unitPrice || '0', 10);
        return acc;
      },
      { count: 0, cents: 0 }
    );
  }

  let busy = false;

  function paint() {
    const { count, cents } = totals();
    rows().forEach((row) => {
      const on = qtyOf(inputOf(row)) > 0;
      row.classList.toggle('is-selected', on);
      $('.wlp-pick__label', row)?.setAttribute('aria-pressed', String(on));
    });
    $$('.wlp-buy__cta, .wlp-sticky__cta').forEach((btn) => {
      if (busy) {
        btn.disabled = true;
        btn.textContent = 'Adding…';
        return;
      }
      btn.disabled = count === 0;
      btn.textContent = count === 0 ? 'Select a flavor' : 'Add ' + count + ' · ' + money(cents);
    });
  }

  /* ---------- steppers, built once per row, safe to re-run --------------- */
  function buildSteppers() {
    rows().forEach((row) => {
      const qty = $('.wlp-pick__qty', row);
      const input = inputOf(row);
      if (!qty || !input || $('.wlp-stepper', qty)) return;

      const name = ($('.wlp-pick__name', row)?.textContent || '').trim();
      const mk = (dir, label) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'wlp-step wlp-step--' + dir;
        b.dataset.weldStep = dir;
        b.setAttribute('aria-label', label + ' ' + name);
        b.textContent = dir === 'up' ? '+' : '−';
        return b;
      };
      const shell = document.createElement('div');
      shell.className = 'wlp-stepper';
      qty.appendChild(shell);
      shell.appendChild(mk('down', 'Remove one'));
      shell.appendChild(input);
      shell.appendChild(mk('up', 'Add one'));
    });
    paint();
  }

  /* ---------- delegated interaction -------------------------------------- */
  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-weld-step]');
    if (step && step.closest(ROOT)) {
      const row = step.closest('.wlp-pick');
      const input = inputOf(row);
      setQty(input, qtyOf(input) + (step.dataset.weldStep === 'up' ? 1 : -1));
      return;
    }

    const face = e.target.closest('.wlp-pick__label');
    if (face && face.closest(ROOT)) {
      const row = face.closest('.wlp-pick');
      const input = inputOf(row);
      setQty(input, qtyOf(input) > 0 ? 0 : 1);
      row.classList.remove('just-picked');
      void row.offsetWidth;                       // restart the animation
      if (qtyOf(input) > 0) row.classList.add('just-picked');
      return;
    }

    if (e.target.closest('[data-weld-dismiss]')) { hideConfirm(); return; }

    if (e.target.closest('[data-weld-retry]')) { renderPanel('Your cart'); return; }

    const rm = e.target.closest('[data-weld-remove]');
    if (rm) { removeLine(rm.dataset.weldRemove); return; }

    // keep the header cart on this page rather than sending them to /cart
    const cartLink = e.target.closest('a[href$="/cart"], cart-icon a, .action__cart');
    if (cartLink) { e.preventDefault(); renderPanel('Your cart'); }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && panel && !panel.hidden) hideConfirm();
  });

  document.addEventListener('input', (e) => {
    if (e.target.classList?.contains('wlp-pick__input')) {
      e.target.value = String(clamp(parseInt(e.target.value, 10)));
      paint();
    }
  });

  /* ---------- add to cart ------------------------------------------------- */
  document.addEventListener('submit', async (event) => {
    const f = event.target;
    if (!f.classList.contains('wlp-buy__form')) return;

    const picked = rows()
      .map((row) => ({ row, input: inputOf(row), qty: qtyOf(inputOf(row)) }))
      .filter((p) => p.qty > 0);

    if (!picked.length || busy) { event.preventDefault(); return; }
    event.preventDefault();

    busy = true; paint();

    const data = new FormData(f);
    const sectionIds = $$('cart-items-component[data-section-id]')
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) data.append('sections', sectionIds.join(','));

    try {
      const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add', {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: data,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.description || json.message || 'Add to cart failed');

      // tag the cart so campaign orders are identifiable in admin.
      // awaited: a concurrent cart write can make the following read look wrong
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ attributes: { 'Landing page': '40-off' } }),
        });
      } catch (e) { /* attribution is best-effort */ }

      picked.forEach((p) => { p.input.value = '0'; });

      const ev = new Event('cart:update', { bubbles: true });
      ev.detail = {
        resource: json,
        sourceId: 'weld-lp-buybox',
        data: { source: 'weld-lp-buybox', itemCount: picked.reduce((n, p) => n + p.qty, 0), sections: json.sections },
      };
      document.dispatchEvent(ev);
      pulseCartIcon();

      const drawer = $('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') drawer.open();
      else if (f.dataset.afterAdd === 'checkout') { window.location.href = '/checkout'; return; }
      else await showConfirm();
    } catch (err) {
      console.error('[weld-lp] add to cart failed', err);
      f.submit();
      return;
    } finally {
      busy = false; paint();
    }
  });

  /* ---------- in-page cart panel ------------------------------------------
     The store has no cart drawer and the header icon points at /cart, which
     drops the customer onto the main site with no route back to the campaign.
     This panel keeps the whole add / review / remove / checkout loop on the
     landing page. */
  let panel;

  function ensurePanel() {
    if (panel && document.body.contains(panel)) return panel;
    panel = document.createElement('div');
    panel.className = 'wlp-confirm';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Your cart');
    panel.hidden = true;
    panel.innerHTML =
      '<div class="wlp-confirm__head">' +
      '<span class="wlp-confirm__tick" aria-hidden="true"></span>' +
      '<p class="wlp-confirm__title"></p>' +
      '<button type="button" class="wlp-confirm__x" data-weld-dismiss aria-label="Close and keep shopping">&times;</button>' +
      '</div>' +
      '<ul class="wlp-confirm__items"></ul>' +
      '<p class="wlp-confirm__empty" hidden>Your cart is empty.</p>' +
      '<p class="wlp-confirm__total"></p>' +
      '<div class="wlp-confirm__actions">' +
      '<a class="wlp-confirm__checkout" href="/checkout">Checkout</a>' +
      '<button type="button" class="wlp-confirm__more" data-weld-dismiss>Keep shopping</button>' +
      '</div>';
    document.body.appendChild(panel);
    return panel;
  }

  function hideConfirm() {
    if (panel) { panel.hidden = true; panel.classList.remove('is-in'); }
    document.body.classList.remove('wlp-confirm-open');
  }

  async function getCart(tries = 2) {
    for (let i = 0; i < tries; i++) {
      try {
        const res = await fetch('/cart.js', {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
          credentials: 'same-origin',
        });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const json = await res.json();
        if (!json || !Array.isArray(json.items)) throw new Error('unexpected cart payload');
        return json;
      } catch (err) {
        if (i === tries - 1) {
          console.error('[weld-lp] could not read the cart', err);
          return null;                 // null means "unknown", never "empty"
        }
        await new Promise((r) => setTimeout(r, 400));
      }
    }
    return null;
  }

  async function renderPanel(title) {
    const el = ensurePanel();
    const items = $('.wlp-confirm__items', el);
    const empty = $('.wlp-confirm__empty', el);
    const total = $('.wlp-confirm__total', el);
    const checkout = $('.wlp-confirm__checkout', el);

    $('.wlp-confirm__title', el).textContent = title;
    el.hidden = false;
    document.body.classList.add('wlp-confirm-open');
    requestAnimationFrame(() => el.classList.add('is-in'));

    const cart = await getCart();

    if (!cart) {
      // a failed read is NOT an empty cart — saying so lies about their order
      items.innerHTML = '';
      empty.hidden = false;
      empty.innerHTML =
        'Couldn\u2019t load your cart just now. ' +
        '<button type="button" class="wlp-retry" data-weld-retry>Try again</button>';
      total.textContent = '';
      checkout.hidden = false;         // checkout still works off the real server-side cart
      return null;
    }

    items.innerHTML = cart.items
      .map(
        (i, idx) =>
          '<li>' +
          '<img src="' + (i.image ? i.image.replace(/(\.[a-z]+)(\?|$)/i, '_80x$1$2') : '') + '" alt="" loading="lazy">' +
          '<span class="wlp-ci__name">' + i.quantity + ' \u00d7 ' + i.product_title.split(' \u2014')[0] + '</span>' +
          '<span class="wlp-ci__price">' + money(i.line_price) + '</span>' +
          '<button type="button" class="wlp-ci__x" data-weld-remove="' + (idx + 1) + '" ' +
          'aria-label="Remove ' + i.product_title.split(' \u2014')[0] + '">&times;</button>' +
          '</li>'
      )
      .join('');
    empty.hidden = cart.item_count > 0;
    empty.textContent = 'Your cart is empty.';
    total.textContent = cart.item_count
      ? cart.item_count + (cart.item_count === 1 ? ' item' : ' items') + ' \u00b7 ' + money(cart.total_price)
      : '';
    checkout.hidden = cart.item_count === 0;
    return cart;
  }

  async function showConfirm() {
    const cart = await renderPanel('Added to cart');
    $('.wlp-confirm__checkout', panel)?.focus({ preventScroll: true });
    return cart;
  }

  async function removeLine(line) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: 0 }),
        credentials: 'same-origin',
      });
    } catch (e) { console.error('[weld-lp] remove failed', e); }
    const ev = new Event('cart:update', { bubbles: true });
    ev.detail = { resource: {}, sourceId: 'weld-lp-buybox', data: { source: 'weld-lp-buybox' } };
    document.dispatchEvent(ev);
    await renderPanel('Your cart');
  }

  function pulseCartIcon() {
    const icon = $('cart-icon') || $('.action__cart');
    if (!icon) return;
    icon.classList.remove('wlp-pulse');
    void icon.offsetWidth;
    icon.classList.add('wlp-pulse');
  }

  /* ---------- boot, and survive section re-renders ------------------------ */
  function killPopup() {
    // belt and braces alongside the CSS: the overlay is fixed and full-screen,
    // so anything it covers (the header cart included) is untappable
    $$('.weld-popup, .weld-popup__overlay, .weld-popup__dialog').forEach((el) => el.remove());
    document.body.classList.remove('weld-popup-open');
  }

  /* ---------- bank the landing-page discount code ------------------------
     When this page sells the ORIGINAL products, the price advantage comes from
     a code rather than a cheaper duplicate product. Visiting /discount/<code>
     stores it on the session so it applies at checkout however the visitor
     arrived — not only from the ad link. Runs at most once per session. */
  function bankDiscount() {
    var host = document.querySelector('[data-discount-code]');
    if (!host) return;
    var code = host.getAttribute('data-discount-code');
    if (!code) return;
    if (document.cookie.indexOf('discount_code=') > -1) return;
    try {
      if (sessionStorage.getItem('wlp-discount') === code) return;  // already tried
      sessionStorage.setItem('wlp-discount', code);
    } catch (e) { return; }   // no storage, no redirect: never risk a loop
    window.location.replace('/discount/' + encodeURIComponent(code) +
      '?redirect=' + encodeURIComponent(window.location.pathname + window.location.search));
  }

  function boot() {
    if (!form()) return;
    bankDiscount();
    killPopup();
    buildSteppers();
  }

  boot();
  // the popup mounts on a timer, so sweep again once it has had time to appear
  [1200, 3000, 6000].forEach((t) => setTimeout(() => { if (form()) killPopup(); }, t));
  document.addEventListener('shopify:section:load', boot);
  new MutationObserver(() => { if (form() && !$('.wlp-stepper')) boot(); })
    .observe(document.body, { childList: true, subtree: true });
})();
